import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play } from "lucide-react";
import Hero from "@/components/site/Hero";
import { ASSETS } from "@/lib/assets";
import api from "@/lib/api";
import EpisodeResources from "@/components/site/EpisodeResources";

export default function Home() {
  const [episodes, setEpisodes] = useState([]);
  const [guests, setGuests] = useState([]);
  const [settings, setSettings] = useState(null);
  const [resources, setResources] = useState([]);

  useEffect(() => {
    api.get("/episodes").then((r) => setEpisodes(r.data)).catch(() => {});
    api.get("/guests").then((r) => setGuests(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/recursos").then((r) => setResources(r.data)).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      <Hero />

      {/* What is MODO PRO */}
      <section data-testid="section-what-is" className="relative bg-mp-black py-24 md:py-36 mp-noise">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-12 grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 lg:col-span-5 lg:col-start-1">
            <div className="mp-eyebrow mb-6">Modo Pro</div>
            <h2 className="font-display font-bold text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-[-0.02em]">
              Dinero real.<br />
              <span className="text-mp-orange">Decisiones</span> reales.
            </h2>
            <p className="mt-8 text-white/75 text-base md:text-lg leading-relaxed">
              Modo Pro es un podcast creado para los latinos que viven, trabajan, emprenden y
              construyen su futuro en Estados Unidos.
            </p>
            <p className="mt-4 text-white/75 text-base leading-relaxed">
              Hablamos de dinero y de todo lo que termina impactándolo: crédito, negocios,
              bienes raíces, inversiones, impuestos, tecnología, oportunidades y decisiones de vida.
            </p>
            <p className="mt-8 font-display text-xl text-white">
              Porque <span className="text-mp-orange">entender mejor</span> cambia la manera en que decidimos.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:col-start-7 relative">
            <div className="aspect-[4/5] w-full overflow-hidden border border-white/10">
              <img src={ASSETS.eldaGuests} alt="Elda con invitados en Modo Pro" className="h-full w-full object-cover" />
            </div>
            <div className="hidden md:block absolute -bottom-8 -left-8 w-40 border border-mp-orange/60 p-4 bg-mp-black">
              <div className="mp-eyebrow text-mp-orange">Presencial</div>
              <div className="mt-1 font-display text-sm text-white">South Florida</div>
            </div>
          </div>
        </div>
      </section>

      {/* Core statement */}
      <section data-testid="section-core-statement" className="bg-mp-off py-24 md:py-32 border-y border-white/10">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-12">
          <p className="font-display font-medium text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-[1.15] tracking-[-0.015em]">
            Conversamos con <span className="text-mp-orange">empresarios, emprendedores, ejecutivos y expertos</span>{" "}
            para entender cómo funcionan realmente el dinero, los negocios y las oportunidades en
            Estados Unidos. <span className="text-mp-orange">Información, experiencias y perspectivas</span>{" "}
            que ayudan a nuestra comunidad a tomar mejores decisiones.
          </p>
        </div>
      </section>

      {/* Episodes */}
      <section id="episodios" data-testid="section-episodes" className="bg-mp-black py-24 md:py-32">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
          <div className="flex items-end justify-between mb-16">
            <div>
              <div className="mp-eyebrow mb-4">Episodios</div>
              <h2 className="font-display font-bold text-white text-3xl sm:text-4xl lg:text-5xl leading-[1] tracking-[-0.02em] max-w-xl">
                Conversaciones para entender mejor y decidir mejor.
              </h2>
            </div>
            <Link to="/episodios" data-testid="episodes-view-all" className="hidden md:inline-flex mp-link-underline text-white/80 hover:text-white text-sm font-display tracking-[0.24em] uppercase">
              Ver todos <ArrowRight size={14} className="inline ml-2" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
            {episodes.filter((ep) => ep.is_published).slice(0, 6).map((ep) => (
              <EpisodeItem key={ep.id} ep={ep} resources={resources} />
            ))}
          </div>
          {episodes.some((ep) => !ep.is_published) && (
            <div className="mt-24" data-testid="section-episodes-coming">
              <div className="mp-eyebrow mb-6">Próximamente</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
                {episodes.filter((ep) => !ep.is_published).slice(0, 3).map((ep) => (
                  <EpisodeItem key={ep.id} ep={ep} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Guests */}
      <section id="invitados" data-testid="section-guests" className="bg-mp-off py-24 md:py-32 border-t border-white/10">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
          <div className="mp-eyebrow mb-4">Invitados</div>
          <h2 className="font-display font-bold text-white text-3xl sm:text-4xl lg:text-5xl leading-[1] tracking-[-0.02em] max-w-2xl">
            Personas que saben de lo que hablan.
          </h2>
          <p className="mt-6 text-mp-mute font-display text-sm tracking-[0.24em] uppercase">
            Empresarios · Médicos · Emprendedores · Ejecutivos · Creadores · Profesionales · Expertos
          </p>
          {guests.length > 0 ? (
            <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {guests.map((g) => (
                <GuestTile key={g.id} g={g} />
              ))}
            </div>
          ) : (
            <div className="mt-16 border border-white/10 p-10 md:p-16 bg-mp-black">
              <p className="font-display text-white text-xl md:text-2xl leading-tight">
                <span className="text-mp-orange">Expertos, empresarios y profesionales</span> que aportan
                experiencia real, conocimiento y perspectivas que nuestra comunidad puede convertir en
                mejores decisiones.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Elda intro */}
      <section id="elda" data-testid="section-elda" className="bg-mp-black py-24 md:py-36">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-12 grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 lg:col-span-6 order-2 lg:order-1">
            <div className="aspect-[3/4] w-full overflow-hidden border border-white/10 max-w-[520px]">
              <img src={ASSETS.eldaHost} alt="Elda Medina" className="h-full w-full object-cover" />
            </div>
          </div>
          <div className="col-span-12 lg:col-span-6 order-1 lg:order-2">
            <div className="mp-eyebrow mb-6">Elda Medina — Host &amp; Creator</div>
            <h2 className="font-display font-bold text-white text-3xl sm:text-4xl lg:text-5xl leading-[1] tracking-[-0.02em]">
              La conversación empieza con una <span className="text-mp-orange">buena pregunta</span>.
            </h2>
            <p className="mt-8 text-white/75 leading-relaxed">
              Más de dos décadas trabajando entre ventas, marketing, hospitality, real estate,
              emprendimiento, producción y entretenimiento. Elda ha construido su trayectoria
              cruzando industrias que rara vez se hablan entre sí — y desde ahí llega frente al
              micrófono de Modo Pro con una curiosidad afilada por entender cómo funcionan
              realmente las cosas.
            </p>
            <blockquote className="mt-10 border-l-2 border-mp-orange pl-6 font-display text-xl md:text-2xl text-white leading-tight">
              “No necesitas saberlo todo. Necesitas saber a quién preguntarle y qué preguntarle.”
            </blockquote>
            <div className="mt-10">
              <Link to="/elda-medina" data-testid="elda-cta" className="mp-btn-ghost">
                Conoce a Elda <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* La Conversación */}
      <section data-testid="section-conversacion" className="relative bg-mp-black">
        <div className="relative h-[70vh] min-h-[520px] overflow-hidden">
          <img src={ASSETS.studioEmpty} alt="Estudio Modo Pro" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30" />
          <div className="relative z-10 h-full mx-auto max-w-[1440px] px-6 lg:px-12 flex flex-col justify-end pb-16 md:pb-24">
            <div className="mp-eyebrow mb-4">La Conversación</div>
            <h2 className="font-display font-bold text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-[-0.02em] max-w-3xl">
              Conversaciones reales, profundas y prácticas que inspiran{" "}
              <span className="text-mp-orange">mejores decisiones</span>.
            </h2>
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl">
              {[
                ["45–60", "Minutos"],
                ["A + V", "Audio + Video"],
                ["Presencial", "South Florida"],
                ["Español", "Idioma"],
              ].map(([t, l]) => (
                <div key={l} className="border-t border-white/20 pt-4">
                  <div className="font-display text-white text-xl md:text-2xl">{t}</div>
                  <div className="text-mp-mute text-xs tracking-[0.24em] uppercase mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Momentos MODO PRO */}
      <section data-testid="section-momentos" className="bg-mp-off py-24 md:py-32 border-y border-white/10">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
          <div className="mp-eyebrow mb-4">Momentos</div>
          <h2 className="font-display font-bold text-white text-3xl sm:text-4xl lg:text-5xl leading-[1] tracking-[-0.02em] max-w-2xl">
            Momentos <span className="text-mp-orange">Modo Pro</span>.
          </h2>
          <p className="mt-4 text-mp-mute max-w-xl">Reels, TikToks y Shorts. Fragmentos de las conversaciones que ayudan a entender mejor y decidir mejor.</p>
          <div className="mt-12 flex gap-5 overflow-x-auto md:grid md:grid-cols-4 md:overflow-visible mp-scroll-hide -mx-6 lg:mx-0 px-6 lg:px-0">
            {(settings?.moments?.length ? settings.moments : Array.from({ length: 4 }).map((_, i) => ({ placeholder: true, thumbnail: i % 2 === 0 ? ASSETS.eldaGuests : ASSETS.studioEmpty }))).slice(0, 8).map((m, i) => {
              const isPlaceholder = m.placeholder || !m.url;
              const thumb = m.thumbnail || (i % 2 === 0 ? ASSETS.eldaGuests : ASSETS.studioEmpty);
              return isPlaceholder ? (
                <div key={i} data-testid={`moment-${i}`} className="relative shrink-0 w-[240px] md:w-auto aspect-[9/16] overflow-hidden border border-white/10 opacity-70">
                  <img src={thumb} alt="" className="absolute inset-0 h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-black/30" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="text-white font-display text-xs tracking-[0.24em] uppercase">Próximamente</span>
                  </div>
                </div>
              ) : tiktokId(m.url) ? (
                <TikTokMoment key={i} m={m} i={i} thumb={thumb} />
              ) : (
                <a key={i} href={m.url} target="_blank" rel="noreferrer" data-testid={`moment-${i}`} className="relative shrink-0 w-[240px] md:w-auto aspect-[9/16] overflow-hidden border border-white/10 group cursor-pointer">
                  <img src={thumb} alt={m.title || ""} className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full border border-white/80 flex items-center justify-center">
                      <Play size={14} fill="white" className="text-white translate-x-0.5" />
                    </div>
                    <div>
                      <div className="text-mp-orange text-[10px] tracking-[0.24em] uppercase font-display">{m.platform || "Reel"}</div>
                      {m.title && <div className="text-white text-xs mt-0.5 leading-tight line-clamp-1">{m.title}</div>}
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Follow */}
      <section id="contacto" data-testid="section-follow" className="bg-mp-black py-24 md:py-32">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-12 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-6">
            <div className="mp-eyebrow mb-4">Escúchanos</div>
            <h2 className="font-display font-bold text-white text-3xl sm:text-4xl lg:text-5xl leading-[1] tracking-[-0.02em]">
              Nuevas conversaciones.<br /><span className="text-mp-orange">Nuevas perspectivas.</span>
            </h2>
            <p className="mt-6 text-white/70 max-w-md">Sigue Modo Pro y descubre cada nueva conversación.</p>
          </div>
          <div className="col-span-12 lg:col-span-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10">
              {[
                ["Instagram", settings?.instagram, "@modopropodcast"],
                ["YouTube", settings?.youtube, "@ModoProPodcast"],
                ["TikTok", settings?.tiktok, "@modopropodcast"],
                ["Spotify", settings?.spotify, "Próximamente"],
                ["Apple", settings?.apple_podcasts, "Próximamente"],
                ["Email", settings?.email ? `mailto:${settings.email}` : "", settings?.email || "modopropodcast@gmail.com"],
              ].map(([platform, href, sub]) => {
                const disabled = !href;
                return disabled ? (
                  <div key={platform} data-testid={`follow-${platform.toLowerCase()}`} className="bg-mp-black p-6 opacity-60">
                    <div className="font-display text-white text-sm tracking-[0.24em] uppercase">{platform}</div>
                    <div className="mt-2 text-mp-mute text-xs">{sub}</div>
                  </div>
                ) : (
                  <a
                    key={platform}
                    href={href}
                    target={href.startsWith("http") ? "_blank" : undefined}
                    rel="noreferrer"
                    data-testid={`follow-${platform.toLowerCase()}`}
                    className="bg-mp-black p-6 hover:bg-mp-charcoal transition-colors duration-300 group"
                  >
                    <div className="font-display text-white text-sm tracking-[0.24em] uppercase group-hover:text-mp-orange transition-colors duration-300">{platform}</div>
                    <div className="mt-2 text-mp-mute text-xs">{sub}</div>
                  </a>
                );
              })}
            </div>
            <div className="mt-8 text-mp-mute text-sm">
              <a href={`tel:${(settings?.phone || "3057616800").replace(/[^0-9+]/g, "")}`} className="text-white hover:text-mp-orange transition-colors">{settings?.phone || "305-761-6800"}</a> · Producción MODO PRO
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

const tiktokId = (url) => (url || "").match(/tiktok\.com\/.*\/video\/(\d+)/)?.[1];

function TikTokMoment({ m, i, thumb }) {
  const [playing, setPlaying] = useState(false);
  const id = tiktokId(m.url);
  return (
    <div data-testid={`moment-${i}`} className="shrink-0 w-[240px] md:w-auto snap-start">
      <div className="relative aspect-[9/16] overflow-hidden border border-white/10 group bg-black">
        {playing ? (
          <iframe data-testid={`moment-player-${i}`} title={m.title || "TikTok"} src={`https://www.tiktok.com/player/v1/${id}?autoplay=0&description=0&music_info=0`} allow="autoplay; encrypted-media; fullscreen; picture-in-picture" allowFullScreen className="absolute inset-0 h-full w-full" />
        ) : (
          <button type="button" onClick={() => setPlaying(true)} data-testid={`moment-play-${i}`} className="absolute inset-0 w-full h-full text-left cursor-pointer">
            <img src={thumb} alt={m.title || ""} className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 flex items-center gap-3">
              <div className="shrink-0 w-9 h-9 rounded-full border border-white/80 flex items-center justify-center">
                <Play size={14} fill="white" className="text-white translate-x-0.5" />
              </div>
              <div className="min-w-0">
                <div className="text-mp-orange text-[10px] tracking-[0.24em] uppercase font-display">{m.platform || "TikTok"}</div>
                {m.title && <div className="text-white text-xs mt-0.5 leading-tight line-clamp-2">{m.title}</div>}
              </div>
            </div>
          </button>
        )}
      </div>
      <a href={m.url} target="_blank" rel="noreferrer" data-testid={`moment-tiktok-link-${i}`} className="mt-4 inline-flex items-center gap-2 mp-link-underline text-white text-[11px] font-display tracking-[0.24em] uppercase">
        Ver en TikTok <ArrowRight size={12} />
      </a>
    </div>
  );
}

function EpisodeItem({ ep, resources }) {
  const hasLink = ep.youtube_url || ep.spotify_url;
  const cover = ep.thumbnail_url || ASSETS.studioEmpty;
  const goto = ep.youtube_url || ep.spotify_url || "/episodios";
  const external = goto.startsWith("http");
  return (
    <article data-testid={`episode-${ep.id}`} className="group">
      <div className="relative aspect-[16/10] overflow-hidden border border-white/10 bg-mp-charcoal">
        <img src={cover} alt={ep.title} className="h-full w-full object-cover grayscale-[30%] group-hover:grayscale-0 group-hover:scale-[1.03] transition-all duration-700" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        {!ep.is_published && (
          <div className="absolute top-3 left-3 bg-mp-orange text-mp-black font-display text-[10px] tracking-[0.24em] uppercase px-3 py-1">
            Próximamente
          </div>
        )}
      </div>
      <div className="mt-5">
        <div className="text-xs tracking-[0.24em] uppercase text-mp-orange font-display">{ep.profession}</div>
        <h3 className="mt-2 font-display text-white text-xl md:text-2xl leading-tight tracking-[-0.01em]">{ep.title}</h3>
        <p className="mt-1 text-mp-mute text-sm">Con {ep.guest_name}</p>
        {ep.description && <p className="mt-3 text-white/70 text-sm leading-relaxed line-clamp-3">{ep.description}</p>}
        {ep.is_published && hasLink ? (
          <a href={goto} target={external ? "_blank" : undefined} rel="noreferrer" data-testid={`episode-cta-${ep.id}`} className="mt-5 inline-flex items-center gap-2 mp-link-underline text-white text-xs font-display tracking-[0.24em] uppercase">
            Ver Episodio <ArrowRight size={12} />
          </a>
        ) : (
          <span data-testid={`episode-soon-${ep.id}`} className="mt-5 inline-flex items-center gap-2 text-mp-mute text-xs font-display tracking-[0.24em] uppercase">
            Próximamente
          </span>
        )}
        <EpisodeResources ep={ep} resources={resources} />
      </div>
    </article>
  );
}

function GuestTile({ g }) {
  return (
    <div data-testid={`guest-${g.id}`} className="group relative aspect-[3/4] overflow-hidden border border-white/10">
      {g.photo_url ? (
        <img src={g.photo_url} alt={g.name} className="h-full w-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105" />
      ) : (
        <div className="h-full w-full bg-mp-charcoal" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
      <div className="absolute bottom-0 inset-x-0 p-5">
        <div className="text-mp-orange text-[10px] tracking-[0.28em] uppercase font-display">{g.category}</div>
        <div className="mt-1 font-display text-white text-lg leading-tight">{g.name}</div>
        <div className="text-white/70 text-xs mt-1">{g.profession}</div>
      </div>
    </div>
  );
}
